import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useTranslation } from 'localization';
import './LocaleManagerMolecule.scss';

export interface LocaleManagerMoleculeProps {
  locales: string[];
  defaultLocale: string;
  onLocalesChange: (locales: string[]) => void;
  /** Called when the user sets a new default locale. */
  onDefaultLocaleChange?: (locale: string) => void;
  className?: string;
}

const AVAILABLE_LOCALES = [
  { code: 'en-US', label: 'English (US)', shortCode: 'en', countryCode: 'US' },
  { code: 'fr-FR', label: 'Français (France)', shortCode: 'fr', countryCode: 'FR' },
  { code: 'es-ES', label: 'Español (Spain)', shortCode: 'es', countryCode: 'ES' },
  { code: 'de-DE', label: 'Deutsch', shortCode: 'de', countryCode: 'DE' },
  { code: 'pt-BR', label: 'Português (Brazil)', shortCode: 'pt', countryCode: 'PT' },
  { code: 'it-IT', label: 'Italiano', shortCode: 'it', countryCode: 'IT' },
  { code: 'nl-NL', label: 'Nederlands', shortCode: 'nl', countryCode: 'NL' },
  { code: 'ja-JP', label: '日本語', shortCode: 'ja', countryCode: 'JP' },
  { code: 'zh-CN', label: '中文 (Simplified)', shortCode: 'zh', countryCode: 'CN' },
];

type PanelTab = 'add' | 'setDefault';

const LocaleManagerMolecule: React.FC<LocaleManagerMoleculeProps> = ({
  locales,
  defaultLocale,
  onLocalesChange,
  onDefaultLocaleChange,
  className = '',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<PanelTab>('add');
  const containerRef = useRef<HTMLDivElement>(null);

  const available = AVAILABLE_LOCALES.filter((l) => !locales.includes(l.code));

  const getLocaleLabel = (code: string) =>
    AVAILABLE_LOCALES.find((l) => l.code === code)?.label ?? code;

  const getLocaleCountryCode = (code: string) =>
    AVAILABLE_LOCALES.find((l) => l.code === code)?.countryCode ?? code.slice(0, 2).toUpperCase();

  const getLocaleShortCode = (code: string) =>
    AVAILABLE_LOCALES.find((l) => l.code === code)?.shortCode ?? code.slice(0, 2).toLowerCase();

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleAdd = useCallback(
    (code: string) => {
      onLocalesChange([...locales, code]);
    },
    [locales, onLocalesChange],
  );

  const handleRemove = useCallback(
    (locale: string) => {
      if (locale === defaultLocale) return;
      onLocalesChange(locales.filter((l) => l !== locale));
    },
    [locales, defaultLocale, onLocalesChange],
  );

  const handleSetDefault = useCallback(
    (locale: string) => {
      if (onDefaultLocaleChange) onDefaultLocaleChange(locale);
    },
    [onDefaultLocaleChange],
  );

  const classes = ['marketplace-locale-manager', className].filter(Boolean).join(' ');

  return (
    <div className={classes} ref={containerRef}>
      {/* Pill trigger button */}
      <button
        type="button"
        className={`marketplace-locale-manager__trigger ${open ? 'marketplace-locale-manager__trigger--open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className="pi pi-language marketplace-locale-manager__trigger-icon" aria-hidden="true" />
        <span>{t('marketplace.publishers.localeManager.languages')}</span>
        <span className="marketplace-locale-manager__trigger-count">{locales.length}</span>
        <i
          className={`pi pi-chevron-down marketplace-locale-manager__trigger-chevron ${open ? 'marketplace-locale-manager__trigger-chevron--open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Floating panel */}
      {open && (
        <div
          className="marketplace-locale-manager__panel"
          role="dialog"
          aria-label={t('marketplace.publishers.localeManager.panelAriaLabel')}
        >
          {/* Panel tabs */}
          <div className="marketplace-locale-manager__panel-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className={`marketplace-locale-manager__panel-tab ${activeTab === 'add' ? 'marketplace-locale-manager__panel-tab--active' : ''}`}
              aria-selected={activeTab === 'add'}
              onClick={() => setActiveTab('add')}
            >
              {t('marketplace.publishers.localeManager.tabAdd')}
            </button>
            <button
              type="button"
              role="tab"
              className={`marketplace-locale-manager__panel-tab ${activeTab === 'setDefault' ? 'marketplace-locale-manager__panel-tab--active' : ''}`}
              aria-selected={activeTab === 'setDefault'}
              onClick={() => setActiveTab('setDefault')}
            >
              {t('marketplace.publishers.localeManager.tabSetDefault')}
            </button>
          </div>

          {/* Add language tab */}
          {activeTab === 'add' && (
            <ul
              className="marketplace-locale-manager__lang-list"
              role="tabpanel"
            >
              {available.length === 0 ? (
                <li className="marketplace-locale-manager__lang-empty">
                  {t('marketplace.publishers.localeManager.noLanguagesAvailable')}
                </li>
              ) : (
                available.map((locale) => (
                  <li key={locale.code}>
                    <button
                      type="button"
                      className="marketplace-locale-manager__lang-row"
                      onClick={() => {
                        handleAdd(locale.code);
                        setOpen(false);
                      }}
                    >
                      <span className="marketplace-locale-manager__lang-country">
                        {locale.countryCode}
                      </span>
                      <span className="marketplace-locale-manager__lang-name">
                        {locale.label}
                      </span>
                      <span className="marketplace-locale-manager__lang-short">
                        {locale.shortCode}
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}

          {/* Set default tab */}
          {activeTab === 'setDefault' && (
            <ul
              className="marketplace-locale-manager__lang-list"
              role="tabpanel"
            >
              {locales.map((code) => (
                <li key={code}>
                  <button
                    type="button"
                    className={`marketplace-locale-manager__lang-row ${code === defaultLocale ? 'marketplace-locale-manager__lang-row--current-default' : ''}`}
                    onClick={() => {
                      handleSetDefault(code);
                      setOpen(false);
                    }}
                    disabled={code === defaultLocale}
                  >
                    <span className="marketplace-locale-manager__lang-country">
                      {getLocaleCountryCode(code)}
                    </span>
                    <span className="marketplace-locale-manager__lang-name">
                      {getLocaleLabel(code)}
                    </span>
                    {code === defaultLocale && (
                      <span className="marketplace-locale-manager__lang-default-tag">
                        {t('marketplace.publishers.localeTabs.defaultBadge')}
                      </span>
                    )}
                    {code !== defaultLocale && (
                      <span className="marketplace-locale-manager__lang-short">
                        {getLocaleShortCode(code)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

LocaleManagerMolecule.displayName = 'LocaleManagerMolecule';

export default LocaleManagerMolecule;
